import { format, toZonedTime } from 'date-fns-tz';

/**
 * Checks if a date string or Date object is in UTC.
 * - For strings: returns true if ISO format ends with 'Z' or has a timezone offset.
 * - For Date objects: returns true if the time matches UTC midnight (optional, see note).
 */
export function isUTC(date: string | Date): boolean {
    if (!date) return false;

    if (typeof date === 'string') {
        return date.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(date);
    }

    if (date instanceof Date && !isNaN(date.getTime())) {
        // This checks if the time is exactly midnight UTC (optional, adjust as needed)
        return date.getUTCHours() === date.getHours() &&
            date.getUTCMinutes() === date.getMinutes() &&
            date.getUTCSeconds() === date.getSeconds();
    }

    return false;
}

/**
 * Converts a date string or Date object to the user's local time zone and formats it as MM/dd/yyyy.
 * @param date Date string or Date object
 * @param userTimeZone IANA time zone string (e.g., 'America/New_York')
 */
export function getLocalDate(date: string | Date, userTimeZone: string): string {
    if (!date) return '';
    let utcDate: Date;
    if (typeof date === 'string') {
        utcDate = new Date(date);
    } else {
        utcDate = date;
    }
    if (isNaN(utcDate.getTime())) return '';
    const zonedDate = toZonedTime(utcDate, userTimeZone);
    return format(zonedDate, 'MM/dd/yyyy', { timeZone: userTimeZone });
}

/**
 * Displays a date string or Date object as a date-only value in the user's browser time zone.
 * If the input is not UTC, treats it as local and formats accordingly.
 * @param date Date string or Date object
 * @param userTimeZone IANA time zone string
 */
export function displayDate(date: string | Date, userTimeZone: string): string {
    if (!isUTC(date)) {
        // Treat as local date
        let localDate: Date;
        if (typeof date === 'string') {
            localDate = new Date(date);
        } else {
            localDate = date;
        }
        return format(toZonedTime(localDate, userTimeZone), 'MM/dd/yyyy', { timeZone: userTimeZone });
    } else {
        // Already UTC, use normal logic
        return getLocalDate(date, userTimeZone);
    }
}

/**
 * Returns a Date object set to UTC midnight (or noon) for today.
 * @param hour Optional hour (default 0 for midnight, use 12 for noon to avoid date shifting)
 * @param minute Optional minute (default 0)
 * @param second Optional second (default 0)
 * @param ms Optional milliseconds (default 0)
 */
export function getDefaultUtcDate(hour: number = 0, minute: number = 0, second: number = 0, ms: number = 0): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute, second, ms));
}

export function getDefaultLocalDate(hour: number = 0, minute: number = 0, second: number = 0, ms: number = 0): Date {
    const now = new Date();
    // Use local year, month, date, hour, etc.
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, second, ms);
}
